/**
 * 내 배차 — 나에게 배정된 운행을 달별로(2026-10-21 way).
 *
 * 홈 「내 배차 신청」(입찰 결과)이 입찰 버튼과 겹쳐, 배정된 일정을 보는 화면으로 바꿨다. 신청 결과는 입찰 화면 탭.
 * 🔴 관리기사A(직영기사)는 일정만 — 운행료는 서버가 아예 안 보낸다(`showCost`·`cost_driver` null).
 *    관리기사B 등은 줄마다 운행료 + 그 달 합계(합계는 서버가 더한다 · 화면 합산 금지).
 * 통근(10-21) = buslink 날짜별 배차를 서버가 붙여 준다(`commute` · 운행료 없음 · buslink 가 7일 앞까지 펼친다).
 * 차주(10-30 way 통화 · 관리기사B 차주 「내가 차주로 있는 모든 차량을 차량별로」) = 서버가 자기 차량 관리기사B 줄까지 보낸다 —
 *    위에 차량 고르기(전체 · 차량마다) · 다른 기사 줄엔 「○○ 기사」. 합계는 서버 값(전체 = month_total · 한 차 = driver_total).
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { driverApi, driverError, type CommuteRun, type MyDispt, type MyDisptPage } from '../lib/driverApi';
import { useDriverList } from '../lib/driverCache';
import { Icon } from './icons';
import Screen, { ErrorBanner, Loading, shortCar, splitAlias } from './Screen';
import { monthLabel, won } from './format';

const WEEK = ['일', '월', '화', '수', '목', '금', '토'];

function kstToday(): string {
  return new Date(Date.now() + 9 * 3600_000).toISOString().slice(0, 10);
}

function shiftMonth(mon: string, by: number): string {
  const [y, m] = mon.split('-').map(Number);
  const d = new Date(Date.UTC(y ?? 2000, (m ?? 1) - 1 + by, 1));
  return d.toISOString().slice(0, 7);
}

function dayHead(day: string): { label: string; week: string; sun: boolean; sat: boolean } {
  const [y, m, d] = day.split('-').map(Number);
  const w = new Date(Date.UTC(y ?? 2000, (m ?? 1) - 1, d ?? 1)).getUTCDay();
  return { label: `${m}월 ${d}일`, week: WEEK[w] ?? '', sun: w === 0, sat: w === 6 };
}

const hm = (s: string | null) => (s && s.length >= 16 ? s.slice(11, 16) : '');

export default function MyDispts() {
  // 이번 달은 홈에서 미리 받아 둔 것을 바로 그린다(lib/driverCache.ts). 다른 달은 넘길 때 받는다.
  const cur = useDriverList('myDispts', '배차를 불러오지 못했습니다.');
  const [mon, setMon] = useState<string | null>(null);
  const [other, setOther] = useState<MyDisptPage | null>(null);
  const [otherErr, setOtherErr] = useState<string | null>(null);

  const thisMon = cur.data?.mon ?? kstToday().slice(0, 7);
  const shown = mon ?? thisMon;
  const page: MyDisptPage | null = mon && mon !== thisMon ? other : (cur.data ?? null);

  useEffect(() => {
    if (!mon || mon === thisMon) return;
    let alive = true;
    setOther(null);
    setOtherErr(null);
    driverApi
      .listMyDispts({ mon })
      .then((r) => alive && setOther(r))
      .catch((e) => alive && setOtherErr(driverError(e, '배차를 불러오지 못했습니다.')));
    return () => {
      alive = false;
    };
  }, [mon, thisMon]);

  // 차주 차량 고르기(null = 전체). 고르는 단위는 기사(= 그 차를 모는 사람) — 통근 차량번호는 buslink 손 입력이라 차 번호로는 못 묶는다.
  const [pick, setPick] = useState<number | null>(null);
  const fleetChips = useMemo(() => {
    const m = new Map<number, { id: number; name: string | null; other: boolean; cars: string[] }>();
    const add = (id: number | undefined, name: string | null | undefined, other: boolean | undefined, car: string | null | undefined) => {
      if (id == null) return;
      const c = m.get(id) ?? { id, name: name ?? null, other: !!other, cars: [] };
      if (car && !c.cars.includes(car)) c.cars.push(car);
      m.set(id, c);
    };
    for (const c of page?.cars ?? []) add(Number(c.driver_id), c.driver_name, c.other_driver, c.car_no);
    for (const r of page?.rows ?? []) add(r.driver_id, r.driver_name, r.other_driver, r.car_no);
    return [...m.values()].sort((a, b) => Number(a.other) - Number(b.other));
  }, [page]);
  const fleetView = fleetChips.some((c) => c.other);
  // 타일마다 이 달 건수(일반+통근) — 고르기 전에 어느 차에 배차가 있는지 보이게.
  const cntBy = useMemo(() => {
    const m = new Map<number, number>();
    for (const r of page?.rows ?? []) if (r.driver_id != null) m.set(r.driver_id, (m.get(r.driver_id) ?? 0) + 1);
    for (const c of page?.commute ?? []) if (c.driver_id != null) m.set(c.driver_id, (m.get(c.driver_id) ?? 0) + 1);
    return m;
  }, [page]);
  const allCnt = (page?.rows?.length ?? 0) + (page?.commute?.length ?? 0);
  const picked = fleetView && pick !== null && fleetChips.some((c) => c.id === pick) ? pick : null;
  const rowsShown = useMemo(
    () => (page?.rows ?? []).filter((r) => picked === null || r.driver_id === picked),
    [page, picked],
  );
  const commuteShown = useMemo(
    () => (page?.commute ?? []).filter((c) => picked === null || c.driver_id === picked),
    [page, picked],
  );

  const today = kstToday();
  // 일반·통근을 날짜별로 한 줄에 — 같은 날은 시각 순.
  const days = useMemo(() => {
    const m = new Map<string, Item[]>();
    for (const r of rowsShown) {
      const d = (r.start_time ?? '').slice(0, 10) || '날짜 없음';
      m.set(d, [...(m.get(d) ?? []), { kind: 'g', at: hm(r.start_time), r }]);
    }
    for (const c of commuteShown) {
      m.set(c.date, [...(m.get(c.date) ?? []), { kind: 'c', at: c.depart_time ?? '', c }]);
    }
    return [...m.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([d, items]) => [d, items.sort((x, y) => x.at.localeCompare(y.at))] as const);
  }, [rowsShown, commuteShown]);

  const months = cur.data?.months ?? page?.months ?? [];
  // 운행료는 일반 배차에만 있다 — 통근만 있는 달에 「운행료 합계 0원」을 띄우지 않는다.
  const showCost = (page?.showCost ?? cur.data?.showCost ?? false) && rowsShown.length > 0;
  // 합계는 서버가 더한 값 — 전체면 month_total, 한 차(기사)면 driver_total(화면 합산 금지).
  const total = (picked === null ? rowsShown[0]?.month_total : rowsShown[0]?.driver_total) ?? null;
  const commuteCnt = commuteShown.length;
  const upcoming = rowsShown.filter((r) => (r.start_time ?? '') >= today).length
    + commuteShown.filter((c) => c.date >= today).length;
  const error = mon && mon !== thisMon ? otherErr : cur.error;

  return (
    <Screen title="내 배차">
      {/* 달 넘기기 */}
      <div className="driver-card mb-3 flex items-center justify-between gap-2 p-2">
        <button
          className="flex min-h-touch w-14 items-center justify-center rounded-xl text-ink-soft active:bg-canvas"
          aria-label="이전 달"
          onClick={() => setMon(shiftMonth(shown, -1))}
        >
          <Icon name="chevronLeft" className="h-7 w-7" />
        </button>
        <div className="text-center">
          <p className="text-xl font-bold text-ink">{monthLabel(shown)}</p>
          {shown !== thisMon && (
            <button className="text-sm font-semibold text-brand-700" onClick={() => setMon(null)}>
              이번 달로
            </button>
          )}
        </div>
        <button
          className="flex min-h-touch w-14 items-center justify-center rounded-xl text-ink-soft active:bg-canvas"
          aria-label="다음 달"
          onClick={() => setMon(shiftMonth(shown, 1))}
        >
          <Icon name="chevronRight" className="h-7 w-7" />
        </button>
      </div>

      {/* 배차 있는 달 바로 가기 */}
      {months.length > 1 && (
        <div className="-mx-1 mb-4 flex gap-2 overflow-x-auto px-1 pb-1">
          {months.map((m) => (
            <button
              key={m.mon}
              className={`shrink-0 rounded-full px-3 py-1.5 text-sm font-semibold ring-1 ${
                m.mon === shown ? 'bg-brand-600 text-white ring-brand-600' : 'bg-surface text-ink-soft ring-line'
              }`}
              onClick={() => setMon(m.mon === thisMon ? null : m.mon)}
            >
              {Number(m.mon.slice(5))}월 · {m.cnt}건
            </button>
          ))}
        </div>
      )}

      {/* 차주 — 차량 고르기(10-30) */}
      {fleetView && (
        // 드롭다운 한 줄(10-30 way 「버튼이 크다 · 드롭다운 · 고를 수 있는 차량 수 표시」) — 폰에선 기본 선택 창이 떠서 고르기 쉽다.
        <label className="driver-card mb-4 flex items-center gap-3 px-4 py-2">
          <span className="shrink-0 text-base font-semibold text-ink-soft">
            차량 고르기 <span className="text-brand-700">{fleetChips.length}대</span>
          </span>
          <select
            className="min-h-touch min-w-0 flex-1 rounded-xl bg-canvas px-3 text-base font-semibold text-ink ring-1 ring-line"
            value={picked ?? ''}
            onChange={(e) => setPick(e.target.value === '' ? null : Number(e.target.value))}
          >
            <option value="">전체 차량 · {allCnt}건</option>
            {fleetChips.map((c) => {
              const { base, reserve } = splitAlias(c.name);
              const who = c.other ? `${base || '기사'}${reserve ? '(예비)' : ''}` : '본인';
              return (
                <option key={c.id} value={c.id}>
                  {c.cars.map(shortCar).join('·') || '차량 없음'} {who} · {cntBy.get(c.id) ?? 0}건
                </option>
              );
            })}
          </select>
        </label>
      )}

      {error && <ErrorBanner>{error}</ErrorBanner>}

      {!page ? (
        <Loading />
      ) : (
        <>
          {/* 이 달 요약 */}
          <div className={`mb-5 grid gap-2 ${showCost ? 'grid-cols-3' : 'grid-cols-2'}`}>
            <Stat k="이 달 배차" v={`${rowsShown.length + commuteCnt}건`} />
            <Stat k="남은 운행" v={`${upcoming}건`} />
            {showCost && <Stat k="운행료 합계" v={`${won(total)}원`} strong />}
          </div>

          {days.length === 0 ? (
            <p className="driver-card px-4 py-12 text-center text-lg text-ink-soft">이 달에는 배차가 없습니다.</p>
          ) : (
            <ol className="space-y-5">
              {days.map(([day, rows]) => {
                const h = day.length === 10 ? dayHead(day) : null;
                const past = day < today;
                const isToday = day === today;
                return (
                  <li key={day} className={past ? 'opacity-60' : ''}>
                    <div className="mb-2 flex items-center gap-2 px-1">
                      <span className="text-lg font-bold text-ink">{h?.label ?? day}</span>
                      {h && (
                        <span className={`text-base font-semibold ${h.sun ? 'text-red-600' : h.sat ? 'text-brand-700' : 'text-ink-soft'}`}>
                          ({h.week})
                        </span>
                      )}
                      {isToday && <span className="rounded-md bg-leaf px-2 py-0.5 text-sm font-bold text-white">오늘</span>}
                    </div>
                    <ul className="space-y-3">
                      {rows.map((it) =>
                        it.kind === 'g' ? (
                          <DisptCard key={`g${it.r.dispt_car_sch_id}`} r={it.r} showCost={showCost} today={isToday} who={fleetView} />
                        ) : (
                          <CommuteCard key={`c${it.c.id}`} c={it.c} today={isToday} who={fleetView} />
                        ),
                      )}
                    </ul>
                  </li>
                );
              })}
            </ol>
          )}

          {page.commuteError && (
            <p className="mt-4 break-keep rounded-lg bg-amber-50 px-3 py-2 text-base text-amber-900">
              통근 배차를 불러오지 못했습니다. 일반 배차만 보입니다.
            </p>
          )}
          <p className="mt-6 break-keep px-1 text-sm text-ink-soft">
            일반 배차와 통근 배차가 함께 보입니다. 통근은 통근 배차 일정에서 7일 앞까지 보입니다.
            {fleetView && ' 차주이시면 내 차량을 모는 기사님 배차도 함께 보입니다.'}
            {showCost && ' 운행료는 일반 배차만 표시합니다.'}
          </p>
        </>
      )}
    </Screen>
  );
}

type Item = { kind: 'g'; at: string; r: MyDispt } | { kind: 'c'; at: string; c: CommuteRun };

/** 차량번호 끝 4자리(「경기79사3524」 → 「3524」) — 칩이 한 줄에 여럿 들어가게. */

/** 기사 이름 표시 — 「○○ 기사」(차주 화면에서 다른 기사 줄) · 본인 줄은 안 붙인다. */
function WhoTag({ name }: { name: string | null | undefined }) {
  return <span className="rounded-md bg-amber-100 px-2 py-0.5 text-sm font-semibold text-amber-900">{name ?? '다른'} 기사</span>;
}

function Stat({ k, v, strong }: { k: string; v: string; strong?: boolean }) {
  return (
    <div className="driver-card px-3 py-3 text-center">
      <p className="text-sm text-ink-soft">{k}</p>
      <p className={`mt-1 break-keep text-lg font-bold ${strong ? 'text-brand-700' : 'text-ink'}`}>{v}</p>
    </div>
  );
}

function DisptCard({ r, showCost, today, who }: { r: MyDispt; showCost: boolean; today: boolean; who: boolean }) {
  const start = hm(r.start_time);
  const endDay = (r.end_time ?? '').slice(0, 10);
  const startDay = (r.start_time ?? '').slice(0, 10);
  const endClock = r.end_time && !r.end_time.startsWith(`${endDay} 23:59`) ? hm(r.end_time) : '';
  const endText = endDay && endDay !== startDay ? `${Number(endDay.slice(5, 7))}/${Number(endDay.slice(8, 10))} ${endClock}`.trim() : endClock;
  return (
    <li className={`driver-card overflow-hidden ${today ? 'ring-2 ring-leaf' : ''}`}>
      <div className="flex">
        <div className="flex w-20 shrink-0 flex-col items-center justify-center bg-brand-50 px-2 py-4 text-brand-700">
          <span className="text-xl font-bold leading-none">{start || '—'}</span>
          {endText && <span className="mt-1 text-sm">~ {endText}</span>}
        </div>
        <div className="min-w-0 flex-1 p-4">
          <h2 className="break-keep text-lg font-bold leading-snug text-ink">{r.dispt_name || r.cust_name || '배차'}</h2>
          {r.dispt_name && r.cust_name && <p className="mt-0.5 text-base text-ink-soft">{r.cust_name}</p>}
          <p className="mt-2 break-keep text-base text-ink">
            {r.start_loc || '-'} <span className="text-ink-soft">→</span> {r.end_loc || '-'}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-base">
            {r.car_no && <span className="text-ink-soft">{r.car_no}</span>}
            {who && r.other_driver && <WhoTag name={r.driver_name} />}
            {r.trip_type && <span className="rounded-md bg-canvas px-2 py-0.5 text-sm text-ink-soft">{r.trip_type}</span>}
            {showCost && r.cost_driver != null && (
              <span className="ml-auto font-bold text-brand-700">{won(r.cost_driver)}원</span>
            )}
          </div>
          {r.request && <p className="mt-2 break-keep rounded-lg bg-amber-50 px-3 py-2 text-base text-amber-900">{r.request}</p>}
        </div>
      </div>
    </li>
  );
}

function CommuteCard({ c, today, who }: { c: CommuteRun; today: boolean; who: boolean }) {
  return (
    <li className={`driver-card overflow-hidden ${today ? 'ring-2 ring-leaf' : ''}`}>
      <div className="flex">
        <div className="flex w-20 shrink-0 flex-col items-center justify-center bg-green-50 px-2 py-4 text-green-800">
          <span className="text-xl font-bold leading-none">{c.depart_time || '—'}</span>
        </div>
        <div className="min-w-0 flex-1 p-4">
          <h2 className="break-keep text-lg font-bold leading-snug text-ink">{c.route_name || '통근 운행'}</h2>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-base">
            <span className="rounded-md bg-green-100 px-2 py-0.5 text-sm font-semibold text-green-800">통근</span>
            {c.car_no && <span className="text-ink-soft">{c.car_no}</span>}
            {who && c.other_driver && <WhoTag name={c.driver_name} />}
            {c.ran && <span className="text-sm text-ink-soft">운행 기록 있음</span>}
          </div>
        </div>
      </div>
    </li>
  );
}
